import { fetchPharmacyNameById, fetchTourDetails } from "@/libs/api/requests";
import { getLogger } from "@/utils/logger";
import { Dispatch, useCallback, useEffect, useMemo } from "react";
import { useTourDetailContext } from "../data/provider";
import { initialState } from "../data/state";
import { isTruthyAndNotEmpty } from "../guards";
import { TourDetailAction } from "../types";
import {
  createTour,
  getAddressString,
  prepareTourStopList,
} from "../utils/shared";

const log = getLogger("app:libs:tour-detail");

export function useTourDetailPage(tourId: string) {
  const { tour, stops, loading, error, setContext } = useTourDetailContext();

  const fetchDetails = useFetchTourDetails(tourId, setContext);

  useEffect(() => {
    if (stops.length > 0) {
      return;
    }

    setContext({
      type: "new",
      newState: { ...initialState, loading: true },
    });

    fetchDetails();
  }, [fetchDetails, setContext, stops.length]);

  return useMemo(
    () => ({
      tour,
      stops,
      loading,
      error,
      fetchDetails,
    }),
    [tour, stops, loading, error, fetchDetails],
  );
}

export function useFetchTourDetails(
  tourId: string,
  setContext: Dispatch<TourDetailAction>,
) {
  return useCallback(() => {
    fetchTourDetails(tourId)
      .then((response) => {
        const startLocationPromise = fetchPharmacyNameById(
          response.startLocation,
        ).then((response) => response.name);
        const endLocationPromise = fetchPharmacyNameById(
          response.endLocation,
        ).then((response) => response);

        // TODO: improve error handling
        Promise.all([startLocationPromise, endLocationPromise]).then(
          ([startLocation, endLocation]) => {
            if (
              !response.tasks ||
              !response.tasks.length ||
              !startLocation ||
              !endLocation
            ) {
              return;
            }

            const tourTasks = response.tasks.filter(isTruthyAndNotEmpty) || [];
            const tourStops = response.tourStops || [];

            const internalTourStops = prepareTourStopList(tourTasks, tourStops);

            const orderDeliveryStops = internalTourStops.filter(
              (stop) =>
                stop.type === "order-delivery" ||
                stop.type === "gatherTask-dropOff",
            );

            setContext({
              type: "new",
              newState: {
                tour: createTour(
                  response,
                  orderDeliveryStops.length,
                  startLocation,
                  {
                    name: endLocation.name,
                    address: getAddressString(endLocation.address),
                  },
                ),
                stops: internalTourStops,
                loading: false,
                error: null,
              },
            });
          },
        );
      })
      .catch((error) => {
        log("Error: failed to fetch tour details: ", error);

        setContext({
          type: "error",
          error: "Failed to fetch tour details",
        });
      });
  }, [setContext, tourId]);
}
